import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../config/firebase';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';

export const useCanvaIntegration = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isCanvaLoading, setIsCanvaLoading] = useState(false);
  const [isFetchingDesigns, setIsFetchingDesigns] = useState(false);
  const [isRefreshingDesigns, setIsRefreshingDesigns] = useState(false);
  const [isConfiguringCanva, setIsConfiguringCanva] = useState(false);
  const [canvaDesigns, setCanvaDesigns] = useState<any[]>([]);
  const [isCanvaDesignsModalOpen, setIsCanvaDesignsModalOpen] = useState(false);
  const [canvaDesignsPage, setCanvaDesignsPage] = useState(0);
  const [selectedCanvaDesigns, setSelectedCanvaDesigns] = useState<any[]>([]);
  const [isExportingDesigns, setIsExportingDesigns] = useState(false);
  const [isSocialnessModalOpen, setIsSocialnessModalOpen] = useState(false);

  const configureCanvaIntegration = async () => {
    try {
      setIsConfiguringCanva(true);
      const response = await api.get('/management/integrations/canva/config');
      if (response.data.success && response.data.authUrl) {
        // Store full URL (pathname + search) so we can redirect back after OAuth
        const fullPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('oauth_redirect_after_auth', fullPath);
        sessionStorage.setItem('oauth_redirect_timestamp', Date.now().toString());
        sessionStorage.setItem('oauth_canva_config', 'true');
        window.location.href = response.data.authUrl;
      } else {
        showToast(t('integrations.configError', 'Failed to configure Canva integration. Please try again.'), 'error');
      }
    } catch (error) {
      showToast(t('integrations.configError', 'Failed to configure Canva integration. Please try again.'), 'error');
    } finally {
      setIsConfiguringCanva(false);
    }
  };

  const fetchAllDesigns = async (skipOAuthRedirect = false) => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    const token = await user.getIdToken();

    // Canva's API caps each page at 100 designs and returns a `continuation`
    // token for the next page. We walk the cursor until Canva stops handing
    // one back so the UI shows every design the user has, not just the first
    // page. The MAX_PAGES guard keeps accounts with thousands of designs from
    // hammering the worker.
    const PAGE_LIMIT = 100;
    const MAX_PAGES = 20;

    const allRawDesigns: any[] = [];
    let continuation: string | null = null;

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const url = new URL('https://api.socialness.ai/management/integrations/canva/designs');
      url.searchParams.set('limit', String(PAGE_LIMIT));
      if (continuation) url.searchParams.set('continuation', continuation);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // The backend tags token-state errors with `canva_reauth_required` /
          // `requiresReauth`. When we see that flag - or a plain 401/403 - the
          // saved refresh token is unusable and the user has to go back through
          // the OAuth flow. `skipOAuthRedirect` prevents looping when we've just
          // returned from OAuth and still got rejected.
          let requiresReauth = true;
          try {
            const errorBody = await response.clone().json();
            if (errorBody && typeof errorBody === 'object') {
              const code = errorBody.code || errorBody.error?.code;
              requiresReauth = code === 'canva_reauth_required'
                || !!errorBody.requiresReauth
                || !!errorBody.error?.requiresReauth
                || response.status === 401
                || response.status === 403;
            }
          } catch {
            // Non-JSON body: fall through with requiresReauth = true.
          }

          if (skipOAuthRedirect) {
            // We just returned from OAuth but token is still invalid - don't loop
            showToast(t('integrations.canvaAuthFailed', 'Canva authorization failed. Please try reconnecting from the Integrations page.'), 'error');
            return null;
          }
          if (requiresReauth) {
            await configureCanvaIntegration();
          }
          return null;
        }
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (!data.success || !data.data?.designs) break;

      allRawDesigns.push(...data.data.designs);

      const nextContinuation: string | undefined = data.data.continuation;
      if (!nextContinuation) break;
      continuation = nextContinuation;
    }

    const cleaned = allRawDesigns.map((design: any) => {
      const topThumbUrl: string | undefined = design.thumbnail?.url;
      const urlsThumbUrl: string | undefined = design.urls?.thumbnail?.url;
      const resolvedThumbUrl = (topThumbUrl || urlsThumbUrl || '')?.replace?.(/^@/, '');
      return {
        ...design,
        urls: {
          ...design.urls,
          view_url: design.urls?.view_url?.replace(/^@/, '') || design.urls?.view_url,
          edit_url: design.urls?.edit_url?.replace(/^@/, '') || design.urls?.edit_url,
          thumbnail: design.urls?.thumbnail
            ? { ...design.urls.thumbnail, url: (design.urls.thumbnail.url || '').replace(/^@/, '') }
            : design.urls?.thumbnail,
        },
        // Ensure a stable top-level thumbnail field used by the UI
        thumbnail: resolvedThumbUrl ? { url: resolvedThumbUrl } : undefined,
      };
    });
    setCanvaDesigns(cleaned);
    setCanvaDesignsPage(0);
    return cleaned;
  };

  const handleRefreshDesigns = async () => {
    setIsRefreshingDesigns(true);
    try {
      await fetchAllDesigns();
    } finally {
      setIsRefreshingDesigns(false);
    }
  };

  const handleAddFromCanva = async (skipOAuthRedirect = false) => {
    setIsFetchingDesigns(true);
    try {
      const designs = await fetchAllDesigns(skipOAuthRedirect);
      if (designs) setIsCanvaDesignsModalOpen(true);
    } finally {
      setIsFetchingDesigns(false);
    }
  };

  const createNewDesign = async (): Promise<string | null> => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');
      const token = await user.getIdToken();
      const response = await fetch('https://api.socialness.ai/management/integrations/canva/new-design', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`Create design failed: ${response.status}`);
      const data = await response.json();
      return data?.design?.urls?.edit_url || null;
    } catch (e) {
      return null;
    }
  };

  const handleCanvaDesignToggle = (design: any) => {
    setSelectedCanvaDesigns((prev) => {
      const isAlready = prev.some((d) => d.id === design.id);
      return isAlready ? prev.filter((d) => d.id !== design.id) : [...prev, design];
    });
  };

  const exportCanvaDesigns = async (designIds: string[]) => {
    setIsExportingDesigns(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');
      const token = await user.getIdToken();
      const response = await fetch('https://api.socialness.ai/management/integrations/canva/export', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ designs: designIds.map((id) => ({ designId: id })) }),
      });
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const data = await response.json();
      if (data.success && data.data?.export_jobs) {
        const urls: string[] = [];
        for (const job of data.data.export_jobs) {
          if (job.status === 'success' && job.urls && job.urls.length > 0) urls.push(...job.urls);
        }
        if (urls.length > 0) return urls;
        throw new Error('No export URLs received from successful jobs');
      }
      throw new Error('Export request failed or returned no jobs');
    } finally {
      setIsExportingDesigns(false);
    }
  };

  return {
    // state
    isCanvaLoading,
    isFetchingDesigns,
    isRefreshingDesigns,
    isConfiguringCanva,
    canvaDesigns,
    isCanvaDesignsModalOpen,
    canvaDesignsPage,
    selectedCanvaDesigns,
    isExportingDesigns,
    isSocialnessModalOpen,
    // setters
    setIsSocialnessModalOpen,
    setIsCanvaDesignsModalOpen,
    setCanvaDesignsPage,
    setSelectedCanvaDesigns,
    // actions
    configureCanvaIntegration,
    handleRefreshDesigns,
    handleAddFromCanva,
    handleCanvaDesignToggle,
    exportCanvaDesigns,
    createNewDesign,
  };
};


